import React from "react";
import { v4 as uuidv4 } from "uuid";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { I } from "services/lang/LanguageService";
import DatasetButton from "./DatasetButton";
import { ColumnOptions } from "./universal-dataset-table-editor/EditColumn";
import { ICellConfig, IUniversalDatasetTableColumn, IUniversalDatasetTableRow } from "./interfaces";
import { CellType } from "./enums";
import "./UniversalDatasetTable.scss";

interface UniversalDatasetTableRowProps {
  recentData?: DatasetDataInfoResponse[];
  rowData: IUniversalDatasetTableRow;
  columnsOptions: ColumnOptions[];
  compactMode?: boolean;
  maxAge?: number;
  isSelected?: boolean;
  onClick?: () => void;
}

const UniversalDatasetTableRow = ({
  recentData,
  rowData,
  columnsOptions,
  compactMode = false,
  maxAge,
  isSelected,
  onClick,
}: UniversalDatasetTableRowProps) => {
  const renderCellContent = (col: IUniversalDatasetTableColumn, index: number) => {
    if (
      col.cell.type === CellType.STRING ||
      col.cell.type === CellType.NUMBER ||
      col.cell.type === CellType.UNIT
    ) {
      return (
        <div>
          {col.cell.type === CellType.STRING && typeof col.cell.value === "string"
            ? I(col.cell.value)
            : col.cell.value}
        </div>
      );
    }

    if (col.cell.type === CellType.DATASET) {
      const dataset = recentData?.find(ds => ds.identifier === col.cell.value);
      let isExpired = false;
      if (dataset) {
        if (maxAge) {
          const datasetTime = new Date(dataset.data[0].key);
          const elapsedSeconds = (Date.now() - datasetTime.getTime()) / 1000;
          isExpired = elapsedSeconds > maxAge;
        }

        return (
          <DatasetButton
            dataset={dataset}
            columnIndex={index}
            rowData={rowData}
            compactMode={compactMode}
            maxAge={maxAge}
            isExpired={isExpired}
          />
        );
      }
    }
    return null;
  };

  const getColor = (
    minValue?: number,
    maxValue?: number,
    minColor?: string,
    maxColor?: string,
    currentValue?: number
  ): string | undefined => {
    if (currentValue === undefined) return undefined;

    if (minValue !== undefined && minColor && currentValue <= minValue) {
      return minColor;
    }

    if (maxValue !== undefined && maxColor && currentValue >= maxValue) {
      return maxColor;
    }

    return undefined;
  };

  const getTextColor = (cell: ICellConfig, currentValue?: number): string | undefined => {
    if (!cell.setMinMaxColors) return cell.highlightColor?.color;

    const minValue = cell.minMaxValue?.minValue;
    const maxValue = cell.minMaxValue?.maxValue;
    const minColor = cell.minMaxColor?.minTextColor;
    const maxColor = cell.minMaxColor?.maxTextColor;

    const color = getColor(minValue, maxValue, minColor, maxColor, currentValue);
    return color ?? cell.highlightColor?.color;
  };

  const getBgColor = (cell: ICellConfig, currentValue?: number): string | undefined => {
    if (!cell.setMinMaxColors) return cell.highlightColor?.bgColor;

    const minValue = cell.minMaxValue?.minValue;
    const maxValue = cell.minMaxValue?.maxValue;
    const minColor = cell.minMaxColor?.minBgColor;
    const maxColor = cell.minMaxColor?.maxBgColor;

    const color = getColor(minValue, maxValue, minColor, maxColor, currentValue);
    return color ?? cell.highlightColor?.bgColor;
  };

  return (
    <tr onClick={onClick} className={isSelected ? "selected-row" : ""}>
      {rowData.isSeparator ? (
        <td colSpan={columnsOptions.length + 1}>
          <div style={{ height: "15px" }} />
        </td>
      ) : (
        rowData.columns.map((col: IUniversalDatasetTableColumn, index: number) => {
          if (!columnsOptions[index]?.columnVisibility) return null;
          const currentValue = recentData?.find(ds => ds.identifier === col.cell.value)?.data[0]
            ?.value;

          return (
            <td
              key={uuidv4()}
              style={{
                color: getTextColor(col.cell, currentValue),
                backgroundColor: getBgColor(col.cell, currentValue),
              }}
            >
              {renderCellContent(col, index)}
            </td>
          );
        })
      )}
    </tr>
  );
};

export default UniversalDatasetTableRow;
